'use client';

import { createContext, useContext } from 'react';
import Link from 'next/link';
import {
  PortableText,
  PortableTextComponents,
  PortableTextMarkComponentProps,
} from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';
import type { PortableTextBlock } from 'sanity';

import { cn } from '@/lib/utils';
import { GroqImage } from '@/helpers/Image';
import { DEFAULT_LANGUAGE, NON_DEFAULT_LANGUAGES } from '@/sanity/globals/I18n';
import type { BlogImageValue } from '@/sanity/pages/PageBlogPostTemplate';
import { useTrackedLink } from './useTrackedLink';

interface BlogLinkValue {
  _type: 'link';
  href?: string;
  openInNewTab?: boolean;
}

interface BlogQuoteValue {
  _type: 'blogQuote';
  _key: string;
  text?: string;
  author?: string;
}

interface BlogContentValue {
  _type: 'blogBox' | 'blogSection';
  _key: string;
  content?: PortableTextBlock[];
}

const BlogLanguageContext = createContext<string>(DEFAULT_LANGUAGE);

const LANGUAGE_PREFIX = new RegExp(`^/(${NON_DEFAULT_LANGUAGES.join('|')})(?=/|$|[?#])`);

function BlogLink({
  value,
  children,
}: PortableTextMarkComponentProps<BlogLinkValue>) {
  const language = useContext(BlogLanguageContext);
  const raw = stegaClean(value?.href)?.trim() ?? '';
  const isSitePath = /^\/(?![\/\\])/.test(raw);
  const isWeb = /^https?:\/\//i.test(raw);
  const trackedHref = useTrackedLink(
    isSitePath || isWeb ? raw : undefined,
    LANGUAGE_PREFIX.test(raw) ? DEFAULT_LANGUAGE : language,
  );

  if (!isSitePath && !isWeb && !/^(mailto:|tel:|#)/i.test(raw)) {
    return <>{children}</>;
  }

  const href = trackedHref ?? raw;
  const newTab = isWeb || !!value?.openInNewTab;
  const className =
    'text-[#0b6d51] underline decoration-[#0b6d51]/40 underline-offset-[0.2em] transition-colors hover:text-[#003828] hover:decoration-current';

  if (isSitePath && !newTab) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
}

const marks: PortableTextComponents['marks'] = {
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  underline: ({ children }) => <u className="underline">{children}</u>,
  link: BlogLink,
};

const paragraphText = 'font-geist text-[2.25rem] font-normal leading-[1.6] text-[#1c1f22]';
const headingText = 'font-geist font-medium leading-[1.16] tracking-normal text-[#2e3436] [text-wrap:balance]';

const squareBullet =
  'relative pl-[3rem] before:absolute before:left-0 before:top-[calc(0.8em-0.5rem)] before:h-[1rem] before:w-[1rem] before:bg-[#003828]';

const bodyLists: Pick<PortableTextComponents, 'list' | 'listItem'> = {
  list: {
    bullet: ({ value, children }) => (
      <ul className={value.level > 1 ? 'mt-[1.5rem]' : 'mb-[3.6rem]'}>{children}</ul>
    ),
    number: ({ value, children }) => (
      <ol
        className={cn(
          'list-decimal pl-[2.75rem] marker:font-medium marker:text-[#003828]',
          value.level > 1 ? 'mt-[1.5rem]' : 'mb-[3.6rem]',
        )}
      >
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className={cn(paragraphText, squareBullet, 'mb-[1.5rem] last:mb-0')}>
        {children}
      </li>
    ),
    number: ({ children }) => (
      <li className={cn(paragraphText, 'mb-[1.5rem] pl-[0.5rem] last:mb-0')}>
        {children}
      </li>
    ),
  },
};

const sectionComponents: PortableTextComponents = {
  marks,
  ...bodyLists,
  block: {
    h3: ({ children }) => (
      <h3 className={cn(headingText, 'mb-[3rem] text-[3.5rem]')}>{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className={cn(headingText, 'mb-[1.75rem] text-[2.75rem]')}>{children}</h4>
    ),
    normal: ({ children }) => (
      <p className={cn(paragraphText, 'mb-[3rem] w-[min(78.25rem,100%)]')}>{children}</p>
    ),
  },
};

const ruleOnTop =
  'bg-[linear-gradient(rgba(0,0,0,0.1),rgba(0,0,0,0.1))] bg-[length:71.375rem_1px] bg-left-top bg-no-repeat';

const boxText = 'font-geist text-[2rem] font-medium leading-[1.7] tracking-[-0.06rem] text-black/80';

const boxRow = `flex items-center gap-[3rem] ${ruleOnTop} py-[3rem] first:bg-none first:pt-0 last:pb-0`;

const boxComponents: PortableTextComponents = {
  marks,
  block: {
    h3: ({ children }) => (
      <h3 className={cn(headingText, 'mb-[3rem] text-[3.5rem]')}>{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className={cn(headingText, 'mb-[1.75rem] text-[2.75rem]')}>{children}</h4>
    ),
    normal: ({ children }) => <p className={cn(boxText, 'mb-[3rem]')}>{children}</p>,
  },
  list: {
    bullet: ({ value, children }) => (
      <ul className={value.level > 1 ? 'mt-[3rem]' : 'mb-[3rem]'}>{children}</ul>
    ),
    number: ({ value, children }) => (
      <ol className={value.level > 1 ? 'mt-[3rem]' : 'mb-[3rem]'}>{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className={boxRow}>
        <span aria-hidden="true" className="h-[1rem] w-[1rem] shrink-0 bg-[#003828]" />
        <div className={cn(boxText, 'flex-1')}>{children}</div>
      </li>
    ),
    number: ({ index, children }) => (
      <li className={boxRow}>
        <span
          aria-hidden="true"
          className="shrink-0 font-geist text-[2rem] font-medium leading-[1.7] text-[#003828] [font-variant-numeric:tabular-nums]"
        >
          {index + 1}.
        </span>
        <div className={cn(boxText, 'flex-1')}>{children}</div>
      </li>
    ),
  },
};

export const blogPortableText: PortableTextComponents = {
  marks,
  ...bodyLists,
  block: {
    h2: ({ children }) => (
      <h2
        className={cn(
          headingText,
          'mb-[3.5rem] mt-[4.5rem] max-lg:text-[4rem] lg:text-[4.5rem]',
        )}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className={cn(headingText, 'mb-[3rem] mt-[4.5rem] text-[3.5rem]')}>
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className={cn(headingText, 'mb-[1.75rem] mt-[4.5rem] text-[2.75rem]')}>
        {children}
      </h4>
    ),
    normal: ({ children }) => (
      <p className={cn(paragraphText, 'mb-[3.6rem]')}>{children}</p>
    ),
  },
  types: {
    blogImage: ({ value }: { value: BlogImageValue }) => {
      if (!value?.image?.src) return null;

      return (
        <figure className="my-[4.5rem]">
          <GroqImage
            src={value.image}
            alt={value.alt ?? ''}
            quality={100}
            sizes="(min-width: 993px) 40vw, 100vw"
            className="!h-auto !w-full"
          />
          {value.caption && (
            <figcaption className="mt-[1rem] font-geist text-[1.75rem] leading-[1.6] tracking-[-0.0525rem] text-black/80">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    blogQuote: ({ value }: { value: BlogQuoteValue }) => {
      if (!value?.text) return null;

      return (
        <figure className="my-[4.5rem] flex flex-col gap-[3rem] rounded-[0.25rem] bg-[#0a241d] max-lg:p-[5rem] lg:p-[5.75rem]">
          <div className="flex items-start gap-[3rem]">
            <div className="shrink-0 rotate-180">
              <svg
                aria-hidden="true"
                className="h-[3rem] w-[4rem]"
                viewBox="0 0 32 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3107 0V9.85467C13.3107 17.46 8.336 22.6147 1.33333 24L0.00666667 21.132C3.24933 19.9093 5.33333 16.2813 5.33333 13.3333H0V0H13.3107ZM32 0V9.85467C32 17.46 27.0027 22.616 20 24L18.672 21.132C21.916 19.9093 24 16.2813 24 13.3333H18.6893V0H32Z"
                  fill="#12F0B4"
                />
              </svg>
            </div>
            {value.author && (
              <p className="flex-1 font-geist-mono text-[1.75rem] font-semibold uppercase leading-[1.33] text-[#12f0b4] opacity-[0.43]">
                {value.author}
              </p>
            )}
          </div>
          <blockquote className="whitespace-pre-line font-blacker text-[3rem] leading-[1.4] tracking-[-0.09rem] text-white">
            {value.text}
          </blockquote>
        </figure>
      );
    },
    blogBox: ({ value }: { value: BlogContentValue }) => {
      if (!value?.content?.length) return null;

      return (
        <div className="my-[4.5rem] rounded-[1.5rem] bg-[#f7f7f7] p-[4.5rem] [&>*:last-child]:mb-0">
          <PortableText value={value.content} components={boxComponents} />
        </div>
      );
    },
    blogSection: ({ value }: { value: BlogContentValue }) => {
      if (!value?.content?.length) return null;

      return (
        <div className="my-[4.5rem] border-l border-[#003828] pl-[3.75rem] [&>*:last-child]:mb-0 [&>p+p]:bg-[linear-gradient(rgba(0,0,0,0.1),rgba(0,0,0,0.1))] [&>p+p]:bg-[length:71.375rem_1px] [&>p+p]:bg-left-top [&>p+p]:bg-no-repeat [&>p+p]:pt-[3rem]">
          <PortableText value={value.content} components={sectionComponents} />
        </div>
      );
    },
    blogDivider: () => <hr className="my-[4.5rem] border-t border-black/10" />,
  },
};

export function BlogPortableText({
  value,
  language,
  className,
}: {
  value?: PortableTextBlock[];
  language?: string;
  className?: string;
}) {
  if (!value?.length) return null;

  return (
    <BlogLanguageContext.Provider value={stegaClean(language) || DEFAULT_LANGUAGE}>
      <div
        className={cn(
          'font-geist [text-wrap:wrap] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&>h2+*]:mt-0 [&>h3+*]:mt-0 [&>h4+*]:mt-0',
          className,
        )}
      >
        <PortableText value={value} components={blogPortableText} />
      </div>
    </BlogLanguageContext.Provider>
  );
}
